import { useState, useCallback } from "react"
import get from "lodash/get"

const NAME_REGEX = /;?name==".*"/gi

/**
 * @description Controls `filter` query param, which is consumed from
 * `withRsqlQuery HOC, depending of search input value and applied filters state
 * @param params {object} Parameters of the hook
 * @param params.query A prop provided by `withRsqlQuery` and  consumed by `entityList`
 * @param params.setQuery Setter of a `query`,
 * @param params.setIsFilterModalOpen Setter of state of a filter modal
 * @returns result {object}
 * @returns result.shouldShowResetFilter Flag should filters button be visible,
 * @returns result.nameState Current value of search input,
 * @returns result.handleSearchInputChange An onChange handler for search input,
 * @returns result.onFilterOk A handler provided to filter modal when apply button pressed
 * @returns result.onFilterCancel A handler provided to filter modal when cancel button pressed
 * */
export default function useFilter({
  query: { filter = ``, pageSize, orderBy },
  setQuery,
  setIsFilterModalOpen,
}) {
  const rsqlString = filter || ``
  let namePart = get(rsqlString.match(NAME_REGEX), `[0]`, ``)

  const shouldShowResetFilter =
    rsqlString.length > 0 && namePart.length !== rsqlString.length

  if (namePart.charAt(0) === `;`) {
    namePart = namePart.substring(1)
  }

  const [nameState, setNameState] = useState(
    namePart.substring(8, namePart.length - 2),
  )

  const handleSearchInputChange = useCallback(
    ({ target: { value } }) => {
      setNameState(value)
      const nameSubstring = `name=="*${value}*"`
      let newRsqlString = rsqlString

      if (newRsqlString.length === 0 && value.length > 0) {
        newRsqlString = nameSubstring
      } else if (value.length === 0) {
        newRsqlString = newRsqlString.replace(NAME_REGEX, ``)
      } else if (NAME_REGEX.test(newRsqlString)) {
        newRsqlString = newRsqlString.replace(NAME_REGEX, `;${nameSubstring}`)
      } else {
        newRsqlString = `${newRsqlString};${nameSubstring}`
      }
      if (newRsqlString.charAt(0) === `;`) {
        newRsqlString = newRsqlString.substring(1)
      }
      setQuery({ filter: newRsqlString, pageSize, orderBy, page: 1 })
    },
    [rsqlString, pageSize, orderBy],
  )

  const resetFilter = useCallback(() => {
    if (namePart.charAt(0) === `;`) {
      namePart = namePart.substring(1)
    }
    setQuery({ filter: namePart, pageSize, orderBy, page: 1 })
  }, [pageSize, orderBy, rsqlString])

  const onFilterOk = useCallback(
    query => {
      // The query from FilterEditor comes without `name` part which is used in search input
      let newQuery = query
      if (namePart.length > 0) {
        if (namePart.charAt(0) === `;`) {
          newQuery = `${query}${namePart}`
        } else {
          newQuery = `${query};${namePart}`
        }
      }
      if (newQuery.charAt(0) === `;`) {
        newQuery = newQuery.substring(1)
      }
      setQuery({ filter: newQuery, pageSize, orderBy, page: 1 })
      setIsFilterModalOpen(false)
    },
    [rsqlString, pageSize, orderBy],
  )

  const onFilterCancel = useCallback(() => {
    setIsFilterModalOpen(false)
  }, [])

  return {
    shouldShowResetFilter,
    nameState,
    handleSearchInputChange,
    onFilterOk,
    onFilterCancel,
    resetFilter,
  }
}
